import { OrderDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IPagination, IPaginationResult } from "app/common/interfaces";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { environment as env } from "../../../../../environments/environment.prod";
import { IFilters } from "../../filters/interfaces/filterInterface";
import {
  LostSaleFilters,
  LostSaleResponse,
  LostSaleSummaryResponse,
  LostSaleQueryParams,
} from "../lost-sale-v2/models/lost-sale.interfaces";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";

const apiUrl = env.API_AVE_ORDER_URL;
const apiReportUrl = env.API_AVE_ORDER_REPORT_URL;
const moduleName = "order_report";

@Injectable({
  providedIn: "root",
})
export class LostSaleService extends AbstractService<
  OrderDetailAttributes,
  OrderDetailAttributes,
  OrderDetailAttributes
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<OrderDetailAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   * * 📍 Used in:
   * - Reportes/Ventas/Venta Perdida (`lost-sale.component.ts`)
   * - Reportes/Ventas/Listados/Entregas Incompletas (`delivery-incomplete.component.ts`)
   */
  findAllWithCount(
    options: IPagination<OrderDetailAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<OrderDetailAttributes>> {
    const hasSearch = !!options.search;

    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<OrderDetailAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.eq(false)),
        andWhere(
          "type.code" as keyof OrderDetailAttributes,
          Ops.eq(DocumentTypeEnum.PEDIDO)
        ),

        dataToFilter.start && dataToFilter.end
          ? andWhere(
              "createdAt",
              Ops.btwnDates(dataToFilter.start, dataToFilter.end)
            )
          : noop(),

        dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end
          ? andWhere(
              "loadDateTime" as keyof OrderDetailAttributes,
              Ops.btwnDates(
                dataToFilter.pickerUpload_start,
                dataToFilter.pickerUpload_end
              )
            )
          : noop(),

        dataToFilter.warehouseId?.length
          ? andWhere(
              "warehouses.id" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.warehouseId)
            )
          : noop(),

        dataToFilter.subRouteId?.length
          ? andWhere(
              "subRoute.id" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.subRouteId)
            )
          : noop(),

        dataToFilter.customerId?.length
          ? andWhere(
              "shipTo.id" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.customerId)
            )
          : noop(),

        dataToFilter.materialId?.length
          ? andWhere(
              "detail.material.code" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.materialId)
            )
          : noop(),

        dataToFilter.status?.length
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop(),
      ];
    };

    const searchBySerial: FilterFn<OrderDetailAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch
        ? andWhere(
            "serial" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          )
        : noop(),
    ];

    const searchByUser: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "user.user_login.username" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchBySubRouteCode: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "subRoute.externalKeys.code" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchBySubRouteName: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "subRoute.name" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByRelatedDocs: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "relatedDocuments.externalDocument" as keyof OrderDetailAttributes,
            Ops.in([Ops.like(options.search!, "i")])
          ),
        ]
      : [];

    const searchByShipToCode: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.externalKeys.code" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByShipToName: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.name" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByMaterialCode: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "detail.material.code" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByMaterialName: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "detail.material.name" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByTripId: FilterFn<OrderDetailAttributes>[] =
      hasSearch && !isNaN(Number(options.search))
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "trip.id" as keyof OrderDetailAttributes,
              Ops.eq(Number(options.search))
            ),
          ]
        : [];

    const query = buildQuery<OrderDetailAttributes>(
      ...searchBySerial,
      ...searchByUser,
      ...searchBySubRouteCode,
      ...searchBySubRouteName,
      ...searchByRelatedDocs,
      ...searchByShipToCode,
      ...searchByShipToName,
      ...searchByMaterialCode,
      ...searchByMaterialName,
      ...searchByTripId,
      orderBy(
        options.sort ?? "_id",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiReportUrl + `api/${moduleName}/lost_sale`;

    return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
      filter: query,
    });
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllResumeWithCount(
    options: IPagination<OrderDetailAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<OrderDetailAttributes>> {
    const hasSearch = !!options.search;

    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<OrderDetailAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.eq(false)),
        andWhere(
          "type.code" as keyof OrderDetailAttributes,
          Ops.eq(DocumentTypeEnum.PEDIDO)
        ),

        dataToFilter.start && dataToFilter.end
          ? andWhere(
              "createdAt",
              Ops.btwnDates(dataToFilter.start, dataToFilter.end)
            )
          : noop(),

        dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end
          ? andWhere(
              "loadDateTime" as keyof OrderDetailAttributes,
              Ops.btwnDates(
                dataToFilter.pickerUpload_start,
                dataToFilter.pickerUpload_end
              )
            )
          : noop(),

        dataToFilter.warehouseId?.length
          ? andWhere(
              "warehouses.id" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.warehouseId)
            )
          : noop(),

        dataToFilter.subRouteId?.length
          ? andWhere(
              "subRoute.id" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.subRouteId)
            )
          : noop(),

        dataToFilter.customerId?.length
          ? andWhere(
              "shipTo.id" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.customerId)
            )
          : noop(),

        dataToFilter.materialId?.length
          ? andWhere(
              "detail.material.code" as keyof OrderDetailAttributes,
              Ops.in(...dataToFilter.materialId)
            )
          : noop(),

        dataToFilter.status?.length
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop(),
      ];
    };

    const searchBySerial: FilterFn<OrderDetailAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch
        ? andWhere(
            "serial" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          )
        : noop(),
    ];

    const searchByUser: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "user.user_login.username" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchBySubRouteCode: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "subRoute.externalKeys.code" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchBySubRouteName: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "subRoute.name" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByRelatedDocs: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "relatedDocuments.externalDocument" as keyof OrderDetailAttributes,
            Ops.in([Ops.like(options.search!, "i")])
          ),
        ]
      : [];

    const searchByShipToCode: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.externalKeys.code" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByShipToName: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.name" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByMaterialCode: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "detail.material.code" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByMaterialName: FilterFn<OrderDetailAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "detail.material.name" as keyof OrderDetailAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    const searchByTripId: FilterFn<OrderDetailAttributes>[] =
      hasSearch && !isNaN(Number(options.search))
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "trip.id" as keyof OrderDetailAttributes,
              Ops.eq(Number(options.search))
            ),
          ]
        : [];

    const query = buildQuery<OrderDetailAttributes>(
      ...searchBySerial,
      ...searchByUser,
      ...searchBySubRouteCode,
      ...searchBySubRouteName,
      ...searchByRelatedDocs,
      ...searchByShipToCode,
      ...searchByShipToName,
      ...searchByMaterialCode,
      ...searchByMaterialName,
      ...searchByTripId,
      orderBy(
        options.sort ?? "_id",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiReportUrl + `api/${moduleName}/lost_sale_resume`;

    return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
      filter: query,
    });
  }

  findResumeByFilter(
    filter: CombinedFilter<OrderDetailAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string = apiReportUrl + `api/${moduleName}/lost_sale_resume`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.post<IPaginationResult<OrderDetailAttributes>>(
      API_URL,
      {
        filter: filter,
      },
      { headers }
    );
  }

  // ============= New V2 Methods with Strict Typing =============

  /**
   * Gets lost sale data with proper typing for V2 report
   * @param params Query parameters including filters
   * @returns Observable with typed response
   */
  findLostSaleV2(params: LostSaleQueryParams): Observable<LostSaleResponse> {
    const query = this.buildLostSaleQueryV2(params);
    const API_URL = `${apiReportUrl}api/${moduleName}/lost_sale`;

    return this.http.post<LostSaleResponse>(API_URL, {
      filter: query,
    });
  }

  /**
   * Gets lost sale summary data with proper typing for V2 report
   * @param params Query parameters including filters
   * @returns Observable with typed summary response
   */
  findLostSaleSummaryV2(
    params: LostSaleQueryParams
  ): Observable<LostSaleSummaryResponse> {
    const query = this.buildLostSaleQueryV2(params);
    const API_URL = `${apiReportUrl}api/${moduleName}/lost_sale_resume`;

    return this.http.post<LostSaleSummaryResponse>(API_URL, {
      filter: query,
    });
  }

  /**
   * Export lost sale data for V2 report
   * @param params Query parameters
   * @param contextHeader Export context header
   * @returns Observable for export data
   */
  exportLostSaleV2(
    params: LostSaleQueryParams,
    contextHeader: string
  ): Observable<LostSaleResponse> {
    const query = this.buildLostSaleQueryV2(params);
    const API_URL = `${apiReportUrl}api/${moduleName}/lost_sale`;

    const headers = {
      "x-ave-ctx": contextHeader,
    };

    return this.http.post<LostSaleResponse>(
      API_URL,
      {
        filter: query,
      },
      { headers }
    );
  }

  /**
   * Export lost sale summary for V2 report
   * @param params Query parameters
   * @param contextHeader Export context header
   * @returns Observable for export data
   */
  exportLostSaleSummaryV2(
    params: LostSaleQueryParams,
    contextHeader: string
  ): Observable<LostSaleSummaryResponse> {
    const query = this.buildLostSaleQueryV2(params);
    const API_URL = `${apiReportUrl}api/${moduleName}/lost_sale_resume`;

    const headers = {
      "x-ave-ctx": contextHeader,
    };

    return this.http.post<LostSaleSummaryResponse>(
      API_URL,
      {
        filter: query,
      },
      { headers }
    );
  }

  /**
   * Builds query for V2 lost sale report with strict typing
   * @param params Query parameters
   * @returns Combined filter query
   */
  private buildLostSaleQueryV2(
    params: LostSaleQueryParams
  ): CombinedFilter<OrderDetailAttributes> {
    const {
      filters,
      page: pageNum = 1,
      pageSize = 100,
      orderBy: sortField = "createdAt",
      orderDirection = "DESC",
      search = "",
    } = params;

    const query = buildQuery<OrderDetailAttributes>(
      // Search filters
      where(
        "serial" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "user.user_login.username" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "subRoute.externalKeys.code" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "subRoute.name" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "shipTo.externalKeys.code" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "shipTo.name" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "detail.material.code" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere(
        "detail.material.name" as keyof OrderDetailAttributes,
        Ops.like(search ?? "", "i")
      ),
      orWhere("trip.id" as keyof OrderDetailAttributes, Ops.eq(filters.tripId)),

      // Date range filter
      filters.startDate && filters.endDate
        ? andAllWhere(
            "createdAt",
            Ops.btwnDates(filters.startDate, filters.endDate)
          )
        : noop(),

      // Company filter
      filters.companyId
        ? andAllWhere(
            "companyId" as keyof OrderDetailAttributes,
            Ops.eq(filters.companyId)
          )
        : noop(),

      // Sales office filter
      filters.salesOfficeId
        ? andAllWhere(
            "salesOfficeId" as keyof OrderDetailAttributes,
            Ops.eq(filters.salesOfficeId)
          )
        : noop(),

      // Warehouse filter
      filters.warehouseId && filters.warehouseId.length > 0
        ? andAllWhere(
            "warehouses.id" as keyof OrderDetailAttributes,
            Ops.in(...filters.warehouseId)
          )
        : noop(),

      // Sub route filter
      filters.subRouteId && filters.subRouteId.length > 0
        ? andAllWhere(
            "subRoute.id" as keyof OrderDetailAttributes,
            Ops.in(...filters.subRouteId)
          )
        : noop(),

      // Customer filter
      filters.customerId && filters.customerId.length > 0
        ? andAllWhere(
            "shipTo.id" as keyof OrderDetailAttributes,
            Ops.in(...filters.customerId)
          )
        : noop(),

      // Material filter
      filters.materialId
        ? andAllWhere(
            "detail.material.code" as keyof OrderDetailAttributes,
            Ops.eq(filters.materialId)
          )
        : noop(),

      // Material group filter
      filters.materialGroupId && filters.materialGroupId.length > 0
        ? andAllWhere(
            "detail.material.materialGroups.code" as keyof OrderDetailAttributes,
            Ops.in(...filters.materialGroupId)
          )
        : noop(),

      // Assigned user filter
      filters.assignedUserId && filters.assignedUserId.length > 0
        ? andAllWhere(
            "user.user_login.username" as keyof OrderDetailAttributes,
            Ops.in(...filters.assignedUserId)
          )
        : noop(),

      // Trip filter
      filters.tripId
        ? andAllWhere(
            "trip.id" as keyof OrderDetailAttributes,
            Ops.eq(filters.tripId)
          )
        : noop(),

      // Document status filter
      filters.documentStatus && filters.documentStatus.length > 0
        ? andAllWhere(
            "statusId" as keyof OrderDetailAttributes,
            Ops.in(...filters.documentStatus)
          )
        : noop(),

      // Has lost sale filter
      filters.hasLostSale !== undefined
        ? andAllWhere(
            "hasLostSale" as keyof OrderDetailAttributes,
            Ops.eq(filters.hasLostSale)
          )
        : noop(),

      andAllWhere(
        "type.code" as keyof OrderDetailAttributes,
        DocumentTypeEnum.PEDIDO
      ),

      // Order by and pagination
      orderBy(
        sortField as keyof OrderDetailAttributes,
        orderDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(pageNum, pageSize)
    );

    return query;
  }
}
